// PortfolioScreen.js — Full rebuild on top of uploaded version
// Fixes: padding:46→16, modalHeader padding:40→16, proficiency control
// New: tabs, analytics, per-tab filters/sort, skill level colours,
//      better cards with badges + tech chips, sync-to-profile,
//      completion bars, current-job toggle, enhanced AR, detailed modals

import AsyncStorage from '@react-native-async-storage/async-storage';
let _profUserId = 'default';
try {
  require('@react-native-async-storage/async-storage').default
    .getItem('userToken').then(t => { if (t) _profUserId = t.slice(0, 16); }).catch(() => {});
} catch {}

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView, FlatList,
  SafeAreaView, StatusBar, Dimensions, ActivityIndicator, Alert, Modal,
  Animated, TextInput, KeyboardAvoidingView, Platform, RefreshControl, Share,
} from 'react-native';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import {
  Ionicons, MaterialIcons, FontAwesome5, MaterialCommunityIcons, AntDesign,
} from '@expo/vector-icons';
import portfolioDatabase from '../../services/PortfolioDatabase';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

let LinearGradient;
try { LinearGradient = require('expo-linear-gradient').LinearGradient; } catch {
  LinearGradient = ({ colors, style, children, ...p }) => (
    <View style={[style, { backgroundColor: colors?.[0] || '#0e4e96' }]} {...p}>{children}</View>
  );
}

// ─── API ──────────────────────────────────────────────────────────────────────
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const apiFetch = async (ep, opts = {}) => {
  try {
    const token = await AsyncStorage.getItem('auth_token') || '';
    const r = await fetch(`${API_BASE}${ep}`, {
      headers: { 'Content-Type':'application/json','x-api-key':API_KEY,...(token?{Authorization:`Bearer ${token}`}:{}) },
      ...opts,
    });
    if (!r.ok) return { error:true, status:r.status };
    return r.json();
  } catch (e) { return { error:true, message:e.message }; }
};

const { width, height } = Dimensions.get('window');

// ─── Constants ────────────────────────────────────────────────────────────────
const PROJ_STATUSES    = ['planning','in-progress','completed','on-hold','cancelled'];
const SKILL_CATEGORIES = ['Programming','Frontend','Backend','Database','DevOps','Design','Management','Communication','Other'];
const CERT_STATUSES    = ['active','expired','pending'];
const EMP_TYPES        = ['Full-time','Part-time','Contract','Freelance','Internship'];
const EDU_LEVELS       = ["High School","Associate","Bachelor's","Master's","PhD","Certificate","Diploma","Professional"];
const EDU_STATUSES     = ['completed','in-progress','dropped-out'];
const PROJ_CATEGORIES  = ['Infrastructure','Energy','Healthcare','Transportation','Residential','Commercial','Technology','Web','Mobile','Other'];

const SECTION_META = {
  projects:      { label:'Projects',    icon:'briefcase-outline',  color:'#0e4e96', bg:'#EFF6FF' },
  skills:        { label:'Skills',      icon:'star-outline',       color:'#10B981', bg:'#F0FDF4' },
  workHistory:   { label:'Work',        icon:'business-outline',   color:'#F59E0B', bg:'#FFFBEB' },
  education:     { label:'Education',   icon:'school-outline',     color:'#8B5CF6', bg:'#F5F3FF' },
  certifications:{ label:'Certs',       icon:'ribbon-outline',     color:'#EF4444', bg:'#FEF2F2' },
  analytics:     { label:'Analytics',   icon:'analytics-outline',  color:'#667eea', bg:'#EEF2FF' },
};

const TABS = ['projects','skills','workHistory','education','certifications','analytics'];

const toStr = v => (v === null || v === undefined ? '' : String(v));
const clamp = (n, mn=0, mx=100) => Math.max(mn, Math.min(mx, Math.round(Number.isFinite(n)?n:0)));
const mkId  = () => `${Date.now()}_${Math.random().toString(16).slice(2)}`;

const skillLevelColor = p => p<=25?'#94A3B8':p<=50?'#F59E0B':p<=75?'#0e4e96':'#10B981';
const skillLevelLabel = p => p<=25?'Beginner':p<=50?'Intermediate':p<=75?'Advanced':'Expert';
const projStatusColor = s => ({planning:'#94A3B8','in-progress':'#0e4e96',completed:'#10B981','on-hold':'#F59E0B',cancelled:'#EF4444'}[s]||'#64748B');
const eduStatusColor  = s => ({completed:'#10B981','in-progress':'#3B82F6','dropped-out':'#EF4444'}[s]||'#64748B');
const certStatusColor = s => ({active:'#10B981',expired:'#EF4444',pending:'#F59E0B'}[s]||'#94A3B8');


export default function PortfolioScreen() {
  const navigation = useNavigation();
  const route      = useRoute();

  // ── Tab ──────────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(route?.params?.tab || 'projects');

  // ── Data ─────────────────────────────────────────────────────────────────────
  const [projects,       setProjects]       = useState([]);
  const [skills,         setSkills]         = useState([]);
  const [workHistory,    setWorkHistory]    = useState([]);
  const [education,      setEducation]      = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [statistics,     setStatistics]     = useState(null);

  // ── UI ───────────────────────────────────────────────────────────────────────
  const [isLoading,   setIsLoading]   = useState(true);
  const [refreshing,  setRefreshing]  = useState(false);
  const [search,      setSearch]      = useState('');
  const [showSearch,  setShowSearch]  = useState(false);
  const [filterModal, setFilterModal] = useState(false);
  const [syncingProfile, setSyncingProfile] = useState(false);

  // ── AR ───────────────────────────────────────────────────────────────────────
  const [isARActive,    setIsARActive]    = useState(false);
  const [arMode,        setArMode]        = useState('projects');
  const [currentModel,  setCurrentModel]  = useState(null);
  const [modelScale,    setModelScale]    = useState(1);
  const [showTutorial,  setShowTutorial]  = useState(false);

  // ── Filters per tab ──────────────────────────────────────────────────────────
  const [projStatus,   setProjStatus]   = useState('All');
  const [projCat,      setProjCat]      = useState('All');
  const [projSort,     setProjSort]     = useState('date');
  const [skillCat,     setSkillCat]     = useState('All');
  const [skillLevel,   setSkillLevel]   = useState('All');
  const [skillSort,    setSkillSort]    = useState('proficiency');
  const [workEmpType,  setWorkEmpType]  = useState('All');
  const [workStatus,   setWorkStatus]   = useState('All');
  const [workSort,     setWorkSort]     = useState('date');
  const [eduLevel,     setEduLevel]     = useState('All');
  const [eduStatus,    setEduStatus]    = useState('All');
  const [certStatus,   setCertStatus]   = useState('All');
  const [certCat,      setCertCat]      = useState('All');
  const [certSort,     setCertSort]     = useState('date');

  // ── Analytics ─────────────────────────────────────────────────────────────────
  const [analyticsPeriod, setAnalyticsPeriod] = useState('month');
  const [analyticsTab,    setAnalyticsTab]    = useState('overview');
  const [appStats,        setAppStats]        = useState({ total:0, interviews:0, offers:0, topCompanies:[] });

  // ── Edit modal ───────────────────────────────────────────────────────────────
  const [showEditModal, setShowEditModal] = useState(false);
  const [editCategory,  setEditCategory]  = useState('projects');
  const [editMode,      setEditMode]      = useState('add');
  const [editingItem,   setEditingItem]   = useState(null);
  const [formData,      setFormData]      = useState({});
  const [formErrors,    setFormErrors]    = useState({});
  const [savingForm,    setSavingForm]    = useState(false);

  // ── Item detail ───────────────────────────────────────────────────────────────
  const [selectedItem,    setSelectedItem]    = useState(null);
  const [showItemDetails, setShowItemDetails] = useState(false);
  const [showAddMenu,     setShowAddMenu]     = useState(false);

  // ── Animations ───────────────────────────────────────────────────────────────
  const fadeAnim  = useRef(new Animated.Value(0)).current;
  const scaleAnim = useRef(new Animated.Value(0.95)).current;

  // ── Init ─────────────────────────────────────────────────────────────────────
  useEffect(() => { initPortfolio(); }, []);

  useFocusEffect(useCallback(() => {
    if (portfolioDatabase.initialized) loadAll();
  }, []));

  // ── DB events ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!portfolioDatabase.initialized) return;
    const subs = [
      portfolioDatabase.subscribe('project_added',           p  => setProjects(prev=>[...prev,p])),
      portfolioDatabase.subscribe('project_updated',         p  => setProjects(prev=>prev.map(x=>x.id===p.id?p:x))),
      portfolioDatabase.subscribe('project_deleted',         ({id}) => setProjects(prev=>prev.filter(x=>x.id!==id))),
      portfolioDatabase.subscribe('skill_added',             s  => setSkills(prev=>[...prev,s])),
      portfolioDatabase.subscribe('skill_updated',           s  => setSkills(prev=>prev.map(x=>x.id===s.id?s:x))),
      portfolioDatabase.subscribe('skill_deleted',           ({id}) => setSkills(prev=>prev.filter(x=>x.id!==id))),
      portfolioDatabase.subscribe('work_history_added',      w  => setWorkHistory(prev=>[...prev,w])),
      portfolioDatabase.subscribe('work_history_updated',    w  => setWorkHistory(prev=>prev.map(x=>x.id===w.id?w:x))),
      portfolioDatabase.subscribe('work_history_deleted',    ({id}) => setWorkHistory(prev=>prev.filter(x=>x.id!==id))),
      portfolioDatabase.subscribe('certification_added',     c  => setCertifications(prev=>[...prev,c])),
      portfolioDatabase.subscribe('certification_updated',   c  => setCertifications(prev=>prev.map(x=>x.id===c.id?c:x))),
      portfolioDatabase.subscribe('certification_deleted',   ({id}) => setCertifications(prev=>prev.filter(x=>x.id!==id))),
      portfolioDatabase.subscribe('education_added',         e  => setEducation(prev=>[...prev,e])),
      portfolioDatabase.subscribe('education_updated',       e  => setEducation(prev=>prev.map(x=>x.id===e.id?e:x))),
      portfolioDatabase.subscribe('education_deleted',       ({id}) => setEducation(prev=>prev.filter(x=>x.id!==id))),
      portfolioDatabase.subscribe('database_error',          ({error}) => console.warn('DB error:', error)),
    ];
    return () => subs.forEach(fn => typeof fn === 'function' && fn());
  }, [portfolioDatabase.initialized]);

  const initPortfolio = async () => {
    try {
      setIsLoading(true);
      await portfolioDatabase.initialize();
      await loadAll();
      Animated.parallel([
        Animated.timing(fadeAnim,  { toValue:1, duration:700, useNativeDriver:true }),
        Animated.timing(scaleAnim, { toValue:1, duration:700, useNativeDriver:true }),
      ]).start();
    } catch (e) {
      Alert.alert('Error', 'Failed to initialize portfolio. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAll = async () => {
    try {
      const [pr,sk,ce,wh,ed,stats] = await Promise.all([
        portfolioDatabase.getProjects().catch(()=>[]),
        portfolioDatabase.getSkills().catch(()=>[]),
        portfolioDatabase.getCertifications().catch(()=>[]),
        portfolioDatabase.getWorkHistory().catch(()=>[]),
        portfolioDatabase.getEducation().catch(()=>[]),
        portfolioDatabase.getStatistics().catch(()=>null),
      ]);
      setProjects(pr||[]); setSkills(sk||[]); setCertifications(ce||[]);
      setWorkHistory(wh||[]); setEducation(ed||[]); setStatistics(stats);
      // Load live application stats for analytics
      loadAppStats();
    } catch (e) { console.warn('loadAll:', e?.message); }
  };

  const loadAppStats = async () => {
    try {
      const uid = await AsyncStorage.getItem('userId') || await AsyncStorage.getItem('user_id');
      if (!uid) return;
      const r = await apiFetch(`/api/data/applications?sort=created_at&order=desc&limit=100`);
      if (!r.error) {
        const apps = Array.isArray(r) ? r : (r.data||r.results||[]);
        const byCompany = {};
        apps.forEach(a => { byCompany[a.company||'Unknown'] = (byCompany[a.company||'Unknown']||0)+1; });
        const topCompanies = Object.entries(byCompany).sort((a,b)=>b[1]-a[1]).slice(0,5).map(([company,count])=>({company,count}));
        setAppStats({
          total: apps.length,
          interviews: apps.filter(a=>a.status==='interview').length,
          offers:     apps.filter(a=>a.status==='offer').length,
          topCompanies,
        });
      }
    } catch {}
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadAll();
    setRefreshing(false);
  };


  // ─── Filtering helpers ────────────────────────────────────────────────────────
  const filtered = {
    projects: (() => {
      let f = [...projects];
      const q = search.toLowerCase();
      if (q) f = f.filter(p => [p.title,p.description,p.category].some(x=>(x||'').toLowerCase().includes(q)));
      if (projStatus!=='All') f = f.filter(p => p.status===projStatus);
      if (projCat   !=='All') f = f.filter(p => p.category===projCat);
      f.sort((a,b) => projSort==='title'?(a.title||'').localeCompare(b.title||''):(b.startDate||'').localeCompare(a.startDate||''));
      return f;
    })(),
    skills: (() => {
      let f = [...skills];
      const q = search.toLowerCase();
      if (q) f = f.filter(s => [s.name,s.category].some(x=>(x||'').toLowerCase().includes(q)));
      if (skillCat!=='All') f = f.filter(s => s.category===skillCat);
      if (skillLevel!=='All') {
        const ranges={Beginner:[0,25],Intermediate:[26,50],Advanced:[51,75],Expert:[76,100]};
        const [mn,mx]=ranges[skillLevel]||[0,100];
        f = f.filter(s => s.proficiency>=mn&&s.proficiency<=mx);
      }
      f.sort((a,b) => skillSort==='name'?(a.name||'').localeCompare(b.name||''):(b.proficiency||0)-(a.proficiency||0));
      return f;
    })(),
    workHistory: (() => {
      let f = [...workHistory];
      const q = search.toLowerCase();
      if (q) f = f.filter(w => [w.position,w.company,w.location,w.description].some(x=>(x||'').toLowerCase().includes(q)));
      if (workEmpType!=='All') f = f.filter(w => w.employmentType===workEmpType);
      if (workStatus!=='All') { const isCur=w=>!w.endDate||w.endDate==='Present'||w.currentlyWorking; f=f.filter(w=>workStatus==='Current'?isCur(w):!isCur(w)); }
      f.sort((a,b) => workSort==='company'?(a.company||'').localeCompare(b.company||''):(b.startDate||'').localeCompare(a.startDate||''));
      return f;
    })(),
    education: (() => {
      let f = [...education];
      const q = search.toLowerCase();
      if (q) f = f.filter(e => [e.degree,e.institution,e.fieldOfStudy].some(x=>(x||'').toLowerCase().includes(q)));
      if (eduLevel!=='All') f = f.filter(e => e.level===eduLevel);
      if (eduStatus!=='All') f = f.filter(e => e.status===eduStatus);
      return f;
    })(),
    certifications: (() => {
      let f = [...certifications];
      const q = search.toLowerCase();
      if (q) f = f.filter(c => [c.title,c.issuer].some(x=>(x||'').toLowerCase().includes(q)));
      if (certStatus!=='All') f = f.filter(c => c.status===certStatus);
      return f;
    })(),
  };

  // ─── CRUD ─────────────────────────────────────────────────────────────────────
  const openAdd = (cat) => {
    const defaults = {
      projects:       { title:'',description:'',category:'Technology',status:'planning',startDate:'',endDate:'',url:'',technologies:[],achievements:[] },
      skills:         { name:'',proficiency:50,category:'Programming',description:'',experienceYears:1 },
      certifications: { title:'',issuer:'',date:'',expiryDate:'',credentialId:'',verificationUrl:'',status:'active',category:'Technical',description:'' },
      workHistory:    { position:'',company:'',location:'',startDate:'',endDate:'',description:'',employmentType:'Full-time',achievements:[],currentlyWorking:false },
      education:      { degree:'',institution:'',fieldOfStudy:'',level:"Bachelor's",status:'completed',startDate:'',endDate:'',gpa:'',description:'',location:'',achievements:[] },
    };
    setEditCategory(cat); setEditMode('add'); setEditingItem(null);
    setFormData(defaults[cat]||{}); setFormErrors({});
    setShowEditModal(true); setShowAddMenu(false);
  };

  const openEdit = (cat, item) => {
    setEditCategory(cat); setEditMode('edit'); setEditingItem(item);
    setFormData({...item}); setFormErrors({});
    setShowEditModal(true);
  };

  const validateForm = (cat, data) => {
    const e = {};
    if (cat==='projects')       { if (!data.title?.trim()) e.title='Required'; if (!data.description?.trim()) e.description='Required'; }
    if (cat==='skills')         { if (!data.name?.trim()) e.name='Required'; if (!data.proficiency||data.proficiency<1||data.proficiency>100) e.proficiency='Must be 1–100'; }
    if (cat==='certifications') { if (!data.title?.trim()) e.title='Required'; if (!data.issuer?.trim()) e.issuer='Required'; if (!data.date?.trim()) e.date='Required'; }
    if (cat==='workHistory')    { if (!data.position?.trim()) e.position='Required'; if (!data.company?.trim()) e.company='Required'; if (!data.startDate?.trim()) e.startDate='Required'; }
    if (cat==='education')      { if (!data.degree?.trim()) e.degree='Required'; if (!data.institution?.trim()) e.institution='Required'; }
    return e;
  };

  const saveForm = async () => {
    const errs = validateForm(editCategory, formData);
    if (Object.keys(errs).length) { setFormErrors(errs); Alert.alert('Missing fields','Please fill in all required fields.'); return; }
    setSavingForm(true);
    try {
      const db = portfolioDatabase;
      const isEdit = editMode==='edit';
      const fns = {
        projects:       { add:()=>db.addProject(formData),         upd:()=>db.updateProject(editingItem.id,formData)       },
        skills:         { add:()=>db.addSkill(formData),           upd:()=>db.updateSkill(editingItem.id,formData)         },
        certifications: { add:()=>db.addCertification(formData),   upd:()=>db.updateCertification(editingItem.id,formData) },
        workHistory:    { add:()=>db.addWorkHistory(formData),     upd:()=>db.updateWorkHistory(editingItem.id,formData)   },
        education:      { add:()=>db.addEducation(formData),       upd:()=>db.updateEducation(editingItem.id,formData)     },
      };
      await (isEdit ? fns[editCategory].upd() : fns[editCategory].add());

      // Mirror the save into ProfileManagementService so this item flows into
      // resume / profile screens that share the unified data model.
      try {
        await ProfileManagementService.bootstrap();
        if (editCategory === 'projects') {
          await ProfileManagementService.savePortfolioItem({
            id: isEdit ? editingItem.id : undefined,
            ...formData,
            type: 'project',
          });
        }
        // Skills / certifications / education / work history also live in
        // the main profile shape — refresh the profile so the next read
        // picks up any cross-cutting changes the portfolioDatabase made.
        if (['skills', 'certifications', 'education', 'workHistory'].includes(editCategory)) {
          // Best-effort: don't block UX
          ProfileManagementService.getProfile().catch(() => {});
        }
      } catch (_) {}

      // Optimistically update state
      if (isEdit) {
        const map = {projects:setProjects,skills:setSkills,certifications:setCertifications,workHistory:setWorkHistory,education:setEducation};
        map[editCategory](prev=>prev.map(x=>x.id===editingItem.id?{...x,...formData}:x));
      } else {
        const item = { id:mkId(), ...formData };
        const map = {projects:setProjects,skills:setSkills,certifications:setCertifications,workHistory:setWorkHistory,education:setEducation};
        map[editCategory](prev=>[item,...prev]);
      }
      setShowEditModal(false);
      const labels = {projects:'Project',skills:'Skill',certifications:'Certification',workHistory:'Work experience',education:'Education'};
    } catch (e) { Alert.alert('Error', e?.message||'Failed to save.'); }
    finally { setSavingForm(false); }
  };

  const confirmDelete = (cat, item) => {
    const name = item.title||item.name||item.position||item.degree||'this item';
    Alert.alert('Delete', `Remove "${name}"? This cannot be undone.`, [
      {text:'Cancel',style:'cancel'},
      {text:'Delete',style:'destructive',onPress:async()=>{
        try {
          const fns = {
            projects:()=>portfolioDatabase.deleteProject(item.id),
            skills:()=>portfolioDatabase.deleteSkill(item.id),
            certifications:()=>portfolioDatabase.deleteCertification(item.id),
            workHistory:()=>portfolioDatabase.deleteWorkHistory(item.id),
            education:()=>portfolioDatabase.deleteEducation(item.id),
          };
          await fns[cat]?.();
          const map = {projects:setProjects,skills:setSkills,certifications:setCertifications,workHistory:setWorkHistory,education:setEducation};
          map[cat](prev=>prev.filter(x=>x.id!==item.id));
        } catch (e) { Alert.alert('Error','Could not delete.'); }
      }},
    ]);
  };

  // ─── Sync all data to ProfileScreen ──────────────────────────────────────────
  const syncToProfile = async () => {
    setSyncingProfile(true);
    try {
      const existRaw = await AsyncStorage.getItem('prof_user_profile') || await AsyncStorage.getItem('userProfile');
      const existing = existRaw ? JSON.parse(existRaw) : {};
      const merged = {
        ...existing,
        skills: skills.map(s=>({id:s.id,name:s.name,level:skillLevelLabel(s.proficiency),proficiency:s.proficiency})),
        experience: workHistory.map(w=>({id:w.id,title:w.position,company:w.company,location:w.location,startDate:w.startDate,endDate:w.endDate,current:!!w.currentlyWorking,description:w.description,achievements:w.achievements})),
        education: education.map(e=>({id:e.id,degree:e.degree,institution:e.institution,fieldOfStudy:e.fieldOfStudy,location:e.location,startDate:e.startDate,endDate:e.endDate,gpa:e.gpa,description:e.description})),
        certifications: certifications.map(c=>({id:c.id,name:c.title,issuer:c.issuer,date:c.date,expiry:c.expiryDate,credentialId:c.credentialId})),
        projects: projects.map(p=>({id:p.id,name:p.title,description:p.description,tech:p.technologies?.join(', ')||'',url:p.url,startDate:p.startDate,endDate:p.endDate})),
        preferences: existing.preferences || {},
        privacy:     existing.privacy     || {},
      };
      const j = JSON.stringify(merged);
      await Promise.all([
        AsyncStorage.setItem('prof_user_profile',j),
        AsyncStorage.setItem('userProfile',j),
        AsyncStorage.setItem('professional_default_userProfile',j),
      ]);
      Alert.alert('✅ Synced', `Profile updated:\n• ${skills.length} skills\n• ${workHistory.length} work entries\n• ${education.length} education entries\n• ${certifications.length} certifications\n• ${projects.length} projects`);
    } catch (e) { Alert.alert('Error', e?.message||'Sync failed.'); }
    finally { setSyncingProfile(false); }
  };


  // ─── CARD RENDERS ─────────────────────────────────────────────────────────────

  const Badge = ({ label, color, bg }) => (
    <View style={[t.badge,{backgroundColor:bg||color+'22',borderColor:color+'44'}]}>
      <Text style={[t.badgeT,{color}]}>{label}</Text>
    </View>
  );

  const Tag = ({ label, color='#0e4e96' }) => (
    <View style={[t.tag,{backgroundColor:color+'18',borderColor:color+'33'}]}>
      <Text style={[t.tagT,{color}]}>{label}</Text>
    </View>
  );

  const renderProject = ({ item:p }) => (
    <TouchableOpacity style={t.card} onPress={()=>{setSelectedItem({...p,_cat:'projects'});setShowItemDetails(true);}}>
      <View style={t.cardRow}>
        <View style={[t.cardIcon,{backgroundColor:'#EFF6FF'}]}><Ionicons name="briefcase-outline" size={20} color="#0e4e96"/></View>
        <View style={{flex:1}}>
          <Text style={t.cardTitle}>{toStr(p.title)}</Text>
          <Text style={t.cardSub}>{toStr(p.client||p.category)}</Text>
          <View style={t.metaRow}>
            <Badge label={p.status||'planning'} color={projStatusColor(p.status||'planning')}/>
            {p.category?<Badge label={p.category} color="#0e4e96"/>:null}
          </View>
        </View>
        <View style={t.cardBtns}>
          <TouchableOpacity style={t.editBtn} onPress={()=>openEdit('projects',p)}><Ionicons name="create-outline" size={15} color="#0e4e96"/></TouchableOpacity>
          <TouchableOpacity style={t.delBtn}  onPress={()=>confirmDelete('projects',p)}><Ionicons name="trash-outline" size={15} color="#EF4444"/></TouchableOpacity>
        </View>
      </View>
      {p.description?<Text style={t.cardDesc} numberOfLines={2}>{p.description}</Text>:null}
      {p.technologies?.length>0&&(
        <View style={t.tagRow}>
          {p.technologies.slice(0,4).map((tt,i)=><Tag key={i} label={tt} color="#8B5CF6"/>)}
          {p.technologies.length>4?<Tag label={`+${p.technologies.length-4}`} color="#94A3B8"/>:null}
        </View>
      )}
      <View style={t.cardFooter}>
        {p.startDate?<><Ionicons name="calendar-outline" size={12} color="#94A3B8"/><Text style={t.cardFootT}>{p.startDate}{p.endDate?` – ${p.endDate}`:''}</Text></>:null}
        {p.url?<><Ionicons name="link-outline" size={12} color="#94A3B8" style={{marginLeft:8}}/><Text style={[t.cardFootT,{color:'#0e4e96'}]} numberOfLines={1}>{p.url}</Text></>:null}
      </View>
    </TouchableOpacity>
  );

  const renderSkill = ({ item:sk }) => {
    const lc = skillLevelColor(sk.proficiency||0);
    const lp = clamp(sk.proficiency||0);
    return (
      <TouchableOpacity style={t.card} onPress={()=>{setSelectedItem({...sk,_cat:'skills'});setShowItemDetails(true);}}>
        <View style={t.cardRow}>
          <View style={{flex:1}}>
            <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center'}}>
              <Text style={t.cardTitle}>{toStr(sk.name)}</Text>
              <Badge label={skillLevelLabel(lp)} color={lc}/>
            </View>
            <Text style={t.cardSub}>{toStr(sk.category)}{sk.experienceYears?` · ${sk.experienceYears}yr`:''}</Text>
          </View>
          <View style={[t.cardBtns,{marginLeft:8}]}>
            <TouchableOpacity style={t.editBtn} onPress={()=>openEdit('skills',sk)}><Ionicons name="create-outline" size={15} color="#0e4e96"/></TouchableOpacity>
            <TouchableOpacity style={t.delBtn}  onPress={()=>confirmDelete('skills',sk)}><Ionicons name="trash-outline" size={15} color="#EF4444"/></TouchableOpacity>
          </View>
        </View>
        {sk.description?<Text style={t.cardDesc} numberOfLines={1}>{sk.description}</Text>:null}
        <View style={t.progRow}>
          <View style={t.progTrack}><View style={[t.progFill,{width:`${lp}%`,backgroundColor:lc}]}/></View>
          <Text style={[t.progPct,{color:lc}]}>{lp}%</Text>
        </View>
      </TouchableOpacity>
    );
  };

  const renderWork = ({ item:w }) => {
    const isCurrent = w.currentlyWorking || !w.endDate || w.endDate==='Present';
    return (
      <TouchableOpacity style={t.card} onPress={()=>{setSelectedItem({...w,_cat:'workHistory'});setShowItemDetails(true);}}>
        <View style={t.cardRow}>
          <View style={[t.cardIcon,{backgroundColor:'#FFFBEB'}]}><Ionicons name="business-outline" size={20} color="#F59E0B"/></View>
          <View style={{flex:1}}>
            <Text style={t.cardTitle}>{toStr(w.position)}</Text>
            <Text style={t.cardSub}>{toStr(w.company)}{w.location?` · ${w.location}`:''}</Text>
            <View style={t.metaRow}>
              <Badge label={isCurrent?'Current':'Previous'} color={isCurrent?'#16A34A':'#64748B'}/>
              {w.employmentType?<Badge label={w.employmentType} color="#F59E0B"/>:null}
            </View>
          </View>
          <View style={t.cardBtns}>
            <TouchableOpacity style={t.editBtn} onPress={()=>openEdit('workHistory',w)}><Ionicons name="create-outline" size={15} color="#0e4e96"/></TouchableOpacity>
            <TouchableOpacity style={t.delBtn}  onPress={()=>confirmDelete('workHistory',w)}><Ionicons name="trash-outline" size={15} color="#EF4444"/></TouchableOpacity>
          </View>
        </View>
        {w.description?<Text style={t.cardDesc} numberOfLines={2}>{w.description}</Text>:null}
        <View style={t.cardFooter}>
          <Ionicons name="calendar-outline" size={12} color="#94A3B8"/>
          <Text style={t.cardFootT}>{w.startDate} – {isCurrent?'Present':w.endDate}</Text>
        </View>
        {w.achievements?.length>0&&(
          <View style={t.tagRow}>
            {w.achievements.slice(0,2).map((a,i)=><View key={i} style={[t.tag,{backgroundColor:'#FFFBEB',borderColor:'#FDE68A'}]}><Text style={[t.tagT,{color:'#92400E'}]} numberOfLines={1}>✓ {a}</Text></View>)}
          </View>
        )}
      </TouchableOpacity>
    );
  };

  const renderEdu = ({ item:e }) => (
    <TouchableOpacity style={t.card} onPress={()=>{setSelectedItem({...e,_cat:'education'});setShowItemDetails(true);}}>
      <View style={t.cardRow}>
        <View style={[t.cardIcon,{backgroundColor:'#F5F3FF'}]}><Ionicons name="school-outline" size={20} color="#8B5CF6"/></View>
        <View style={{flex:1}}>
          <Text style={t.cardTitle}>{toStr(e.degree)}</Text>
          <Text style={t.cardSub}>{toStr(e.institution)}</Text>
          {e.fieldOfStudy?<Text style={[t.cardNote,{marginTop:2}]}>{e.fieldOfStudy}</Text>:null}
          <View style={t.metaRow}>
            <Badge label={e.status||'completed'} color={eduStatusColor(e.status||'completed')}/>
            {e.level?<Badge label={e.level} color="#8B5CF6"/>:null}
          </View>
        </View>
        <View style={t.cardBtns}>
          <TouchableOpacity style={t.editBtn} onPress={()=>openEdit('education',e)}><Ionicons name="create-outline" size={15} color="#0e4e96"/></TouchableOpacity>
          <TouchableOpacity style={t.delBtn}  onPress={()=>confirmDelete('education',e)}><Ionicons name="trash-outline" size={15} color="#EF4444"/></TouchableOpacity>
        </View>
      </View>
      {e.description?<Text style={t.cardDesc} numberOfLines={2}>{e.description}</Text>:null}
      <View style={t.cardFooter}>
        {e.startDate?<><Ionicons name="calendar-outline" size={12} color="#94A3B8"/><Text style={t.cardFootT}>{e.startDate}{e.endDate?` – ${e.endDate}`:''}</Text></>:null}
        {e.gpa?<><Ionicons name="school-outline" size={12} color="#94A3B8" style={{marginLeft:8}}/><Text style={t.cardFootT}>GPA: {e.gpa}</Text></>:null}
        {e.location?<><Ionicons name="location-outline" size={12} color="#94A3B8" style={{marginLeft:8}}/><Text style={t.cardFootT}>{e.location}</Text></>:null}
      </View>
    </TouchableOpacity>
  );

  const renderCert = ({ item:c }) => (
    <TouchableOpacity style={t.card} onPress={()=>{setSelectedItem({...c,_cat:'certifications'});setShowItemDetails(true);}}>
      <View style={t.cardRow}>
        <View style={[t.cardIcon,{backgroundColor:'#FEF2F2'}]}><Ionicons name="ribbon-outline" size={20} color="#EF4444"/></View>
        <View style={{flex:1}}>
          <Text style={t.cardTitle}>{toStr(c.title)}</Text>
          <Text style={t.cardSub}>{toStr(c.issuer)}</Text>
          <View style={t.metaRow}>
            <Badge label={c.status||'active'} color={certStatusColor(c.status||'active')}/>
            {c.category?<Badge label={c.category} color="#EF4444"/>:null}
          </View>
        </View>
        <View style={t.cardBtns}>
          <TouchableOpacity style={t.editBtn} onPress={()=>openEdit('certifications',c)}><Ionicons name="create-outline" size={15} color="#0e4e96"/></TouchableOpacity>
          <TouchableOpacity style={t.delBtn}  onPress={()=>confirmDelete('certifications',c)}><Ionicons name="trash-outline" size={15} color="#EF4444"/></TouchableOpacity>
        </View>
      </View>
      <View style={t.cardFooter}>
        {c.date?<><Ionicons name="calendar-outline" size={12} color="#94A3B8"/><Text style={t.cardFootT}>Issued: {c.date}</Text></>:null}
        {c.expiryDate?<><Ionicons name="time-outline" size={12} color="#94A3B8" style={{marginLeft:8}}/><Text style={t.cardFootT}>Exp: {c.expiryDate}</Text></>:null}
      </View>
      {c.credentialId?<Text style={t.cardNote}>ID: {c.credentialId}</Text>:null}
      {c.verificationUrl?<Text style={[t.cardNote,{color:'#0e4e96'}]} numberOfLines={1}>{c.verificationUrl}</Text>:null}
    </TouchableOpacity>
  );

  const Empty = ({ cat }) => {
    const m = SECTION_META[cat]||{};
    return (
      <View style={t.empty}>
        <Ionicons name={m.icon||'layers-outline'} size={52} color="#CBD5E1"/>
        <Text style={t.emptyT}>No {m.label||cat} yet</Text>
        <Text style={t.emptyS}>{search?'Try adjusting your search':'Add your first entry to get started'}</Text>
        {!search&&<TouchableOpacity style={[t.emptyBtn,{backgroundColor:m.color||'#0e4e96'}]} onPress={()=>openAdd(cat)}><Text style={t.emptyBtnT}>Add {m.label||cat}</Text></TouchableOpacity>}
      </View>
    );
  };


  // ─── TAB CONTENT ─────────────────────────────────────────────────────────────

  const TabList = ({ data, renderItem, keyExtractor, cat }) => (
    <FlatList
      data={data}
      renderItem={renderItem}
      keyExtractor={keyExtractor||(item=>item.id||mkId())}
      contentContainerStyle={t.list}
      showsVerticalScrollIndicator={false}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[SECTION_META[cat]?.color||'#0e4e96']}/>}
      ListEmptyComponent={<Empty cat={cat}/>}
    />
  );

  const renderAnalytics = () => {
    const total = projects.length+skills.length+workHistory.length+education.length+certifications.length;
    const completeness = Math.round(
      ([projects,skills,workHistory,education,certifications].filter(a=>a.length>0).length/5)*100
      + (skills.length>=5?10:skills.length*2)
      + (workHistory.length>0?5:0)+(education.length>0?5:0)
    );
    const clampedC = Math.min(100,completeness);
    const aPeriods = [{key:'week',label:'Week'},{key:'month',label:'Month'},{key:'quarter',label:'Quarter'},{key:'year',label:'Year'}];
    const aTabs    = [{key:'overview',label:'Overview',icon:'analytics-outline'},{key:'trends',label:'Trends',icon:'trending-up-outline'},{key:'insights',label:'Insights',icon:'bulb-outline'}];
    return (
      <ScrollView contentContainerStyle={{paddingBottom:60}} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh}/>}>
        {/* Period selector */}
        <View style={t.periodRow}>
          {aPeriods.map(p=>(
            <TouchableOpacity key={p.key} style={[t.periodBtn,analyticsPeriod===p.key&&t.periodBtnA]} onPress={()=>setAnalyticsPeriod(p.key)}>
              <Text style={[t.periodBtnT,analyticsPeriod===p.key&&t.periodBtnTA]}>{p.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
        {/* Sub-tabs */}
        <View style={t.aTabRow}>
          {aTabs.map(tab=>(
            <TouchableOpacity key={tab.key} style={[t.aTab,analyticsTab===tab.key&&t.aTabA]} onPress={()=>setAnalyticsTab(tab.key)}>
              <Ionicons name={tab.icon} size={15} color={analyticsTab===tab.key?'#667eea':'#94A3B8'}/>
              <Text style={[t.aTabT,analyticsTab===tab.key&&t.aTabTA]}>{tab.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
        {/* Profile completeness */}
        <View style={t.aCard}>
          <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
            <Text style={t.aCardTitle}>Portfolio Completeness</Text>
            <Text style={[t.aCardTitle,{color:'#10B981',fontSize:22}]}>{clampedC}%</Text>
          </View>
          <View style={t.complTrack}><View style={[t.complFill,{width:`${clampedC}%`}]}/></View>
          <Text style={{fontSize:12,color:'#94A3B8',marginTop:6}}>{total} total items across all sections</Text>
        </View>

        {analyticsTab==='overview'&&(
          <>
            {/* Metric grid */}
            <View style={t.metricsGrid}>
              {[
                {label:'Projects',   value:projects.length,       colors:['#0e4e96','#1e7be2'], icon:'briefcase-outline'    },
                {label:'Skills',     value:skills.length,         colors:['#10B981','#34D399'], icon:'star-outline'          },
                {label:'Work Hist.', value:workHistory.length,    colors:['#F59E0B','#FBBF24'], icon:'business-outline'     },
                {label:'Education',  value:education.length,      colors:['#8B5CF6','#A78BFA'], icon:'school-outline'       },
                {label:'Certs',      value:certifications.length, colors:['#EF4444','#F87171'], icon:'ribbon-outline'       },
                {label:'Applications',value:appStats.total,       colors:['#667eea','#764ba2'], icon:'paper-plane-outline'  },
              ].map((m,i)=>(
                <LinearGradient key={i} colors={m.colors} start={{x:0,y:0}} end={{x:1,y:1}} style={t.metricCard}>
                  <Ionicons name={m.icon} size={22} color="#FFF"/>
                  <Text style={t.metricVal}>{m.value}</Text>
                  <Text style={t.metricLbl}>{m.label}</Text>
                </LinearGradient>
              ))}
            </View>
            {/* Section progress */}
            <View style={t.aCard}>
              <Text style={t.aCardTitle}>Section Progress</Text>
              {[
                {label:'Projects',       count:projects.length,       target:5,  color:'#0e4e96'},
                {label:'Skills',         count:skills.length,         target:10, color:'#10B981'},
                {label:'Work History',   count:workHistory.length,    target:3,  color:'#F59E0B'},
                {label:'Education',      count:education.length,      target:2,  color:'#8B5CF6'},
                {label:'Certifications', count:certifications.length, target:3,  color:'#EF4444'},
              ].map((sec,i)=>(
                <View key={i} style={t.secProgRow}>
                  <Text style={{width:110,fontSize:13,color:'#374151',fontWeight:'600'}}>{sec.label}</Text>
                  <View style={{flex:1,height:8,backgroundColor:'#E5E7EB',borderRadius:4,overflow:'hidden',marginHorizontal:8}}>
                    <View style={{height:8,borderRadius:4,backgroundColor:sec.color,width:`${Math.min(100,Math.round(sec.count/sec.target*100))}%`}}/>
                  </View>
                  <Text style={{fontSize:13,color:sec.color,fontWeight:'800',width:36,textAlign:'right'}}>{sec.count}/{sec.target}</Text>
                </View>
              ))}
            </View>
            {/* Application stats */}
            {appStats.total>0&&(
              <View style={t.aCard}>
                <Text style={t.aCardTitle}>Job Applications</Text>
                <View style={{flexDirection:'row',justifyContent:'space-around',marginTop:8}}>
                  {[{label:'Total',val:appStats.total},{label:'Interviews',val:appStats.interviews},{label:'Offers',val:appStats.offers}].map((s,i)=>(
                    <View key={i} style={{alignItems:'center'}}>
                      <Text style={{fontSize:28,fontWeight:'900',color:'#667eea'}}>{s.val}</Text>
                      <Text style={{fontSize:12,color:'#94A3B8',fontWeight:'600'}}>{s.label}</Text>
                    </View>
                  ))}
                </View>
                {appStats.topCompanies?.length>0&&(
                  <View style={{marginTop:14}}>
                    <Text style={{fontSize:13,fontWeight:'700',color:'#374151',marginBottom:8}}>Top Applied Companies</Text>
                    {appStats.topCompanies.map((c,i)=>(
                      <View key={i} style={t.compRow}>
                        <View style={t.compRank}><Text style={t.compRankT}>{i+1}</Text></View>
                        <Text style={{flex:1,fontSize:14,fontWeight:'600',color:'#0F172A'}}>{c.company}</Text>
                        <Text style={{fontSize:13,color:'#94A3B8'}}>{c.count} apps</Text>
                      </View>
                    ))}
                  </View>
                )}
              </View>
            )}
          </>
        )}

        {analyticsTab==='trends'&&(
          <>
            <View style={t.aCard}>
              <Text style={t.aCardTitle}>Skill Proficiency Distribution</Text>
              {[
                {label:'Expert (76-100%)',     color:'#10B981', count:skills.filter(s=>s.proficiency>75).length},
                {label:'Advanced (51-75%)',    color:'#0e4e96', count:skills.filter(s=>s.proficiency>50&&s.proficiency<=75).length},
                {label:'Intermediate (26-50%)',color:'#F59E0B', count:skills.filter(s=>s.proficiency>25&&s.proficiency<=50).length},
                {label:'Beginner (0-25%)',     color:'#94A3B8', count:skills.filter(s=>s.proficiency<=25).length},
              ].map((row,i)=>(
                <View key={i} style={t.distRow}>
                  <View style={[t.distDot,{backgroundColor:row.color}]}/>
                  <Text style={{flex:1,fontSize:12,color:'#374151'}}>{row.label}</Text>
                  <View style={{width:100,height:7,backgroundColor:'#E5E7EB',borderRadius:4,overflow:'hidden',marginHorizontal:8}}>
                    <View style={{height:7,borderRadius:4,backgroundColor:row.color,width:`${skills.length?Math.round(row.count/skills.length*100):0}%`}}/>
                  </View>
                  <Text style={{fontSize:13,fontWeight:'800',color:row.color,width:20,textAlign:'right'}}>{row.count}</Text>
                </View>
              ))}
            </View>
            <View style={t.aCard}>
              <Text style={t.aCardTitle}>Employment Types</Text>
              {EMP_TYPES.map((type,i)=>{
                const count=workHistory.filter(w=>w.employmentType===type).length;
                if(!count) return null;
                return(
                  <View key={i} style={t.distRow}>
                    <View style={[t.distDot,{backgroundColor:'#F59E0B'}]}/>
                    <Text style={{flex:1,fontSize:12,color:'#374151'}}>{type}</Text>
                    <View style={{width:100,height:7,backgroundColor:'#E5E7EB',borderRadius:4,overflow:'hidden',marginHorizontal:8}}>
                      <View style={{height:7,borderRadius:4,backgroundColor:'#F59E0B',width:`${workHistory.length?Math.round(count/workHistory.length*100):0}%`}}/>
                    </View>
                    <Text style={{fontSize:13,fontWeight:'800',color:'#F59E0B',width:20,textAlign:'right'}}>{count}</Text>
                  </View>
                );
              }).filter(Boolean)}
            </View>
          </>
        )}

        {analyticsTab==='insights'&&(
          <>
            <LinearGradient colors={['#667eea','#764ba2']} start={{x:0,y:0}} end={{x:1,y:1}} style={[t.aCard,{borderWidth:0}]}>
              <Text style={[t.aCardTitle,{color:'#FFF'}]}>🤖 AI Insights</Text>
              {[
                {emoji:'🎯',title:'Portfolio Strength',text:`Your portfolio has ${total} entries. Profiles with 20+ entries get 2× more recruiter attention.`},
                {emoji:'📈',title:'Skill Coverage',text:`${skills.length} skills listed. Adding ${Math.max(0,10-skills.length)} more with specific frameworks increases ATS match by up to 30%.`},
                {emoji:'💼',title:'Work Experience',text:workHistory.length>0?`${workHistory.length} work entries. Profiles with quantifiable achievements (metrics, %, £) get 65% more interview invites.`:'Add work experience with measurable results to significantly boost your profile score.'},
                {emoji:'🏆',title:'Certifications',text:certifications.length>0?`${certifications.length} certification${certifications.length>1?'s':''} add strong credibility — keep them renewed.`:'Adding industry certifications increases trust score. Consider NEBOSH, PMI, AWS, or relevant professional bodies.'},
              ].map((ins,i)=>(
                <View key={i} style={{flexDirection:'row',gap:10,marginBottom:14}}>
                  <Text style={{fontSize:20}}>{ins.emoji}</Text>
                  <View style={{flex:1}}><Text style={{fontSize:13,fontWeight:'700',color:'#FFF',marginBottom:3}}>{ins.title}</Text><Text style={{fontSize:12,color:'rgba(255,255,255,0.85)',lineHeight:18}}>{ins.text}</Text></View>
                </View>
              ))}
            </LinearGradient>
            <View style={t.aCard}>
              <Text style={t.aCardTitle}>Recommendations</Text>
              {[
                {icon:'star-outline',       label:'Add More Skills',    text:'Aim for 10+ skills covering technical & soft',   color:'#10B981',  action:()=>openAdd('skills')       },
                {icon:'school-outline',     label:'Complete Education',  text:'Add all degrees and qualifications',             color:'#8B5CF6',  action:()=>openAdd('education')    },
                {icon:'document-text-outline',label:'Update Resume',    text:'Sync your portfolio to your profile',            color:'#0e4e96',  action:syncToProfile               },
                {icon:'ribbon-outline',     label:'Get Certified',       text:'Add industry certifications to stand out',      color:'#EF4444',  action:()=>openAdd('certifications')},
                {icon:'briefcase-outline',  label:'Add Projects',        text:'Showcase your best work with tech stack',       color:'#F59E0B',  action:()=>openAdd('projects')     },
              ].map((rec,i)=>(
                <TouchableOpacity key={i} style={{flexDirection:'row',alignItems:'center',paddingVertical:11,borderBottomWidth:1,borderBottomColor:'#F1F5F9',gap:10}} onPress={rec.action}>
                  <View style={{width:36,height:36,borderRadius:10,backgroundColor:rec.color+'20',justifyContent:'center',alignItems:'center'}}><Ionicons name={rec.icon} size={17} color={rec.color}/></View>
                  <View style={{flex:1}}><Text style={{fontSize:14,fontWeight:'700',color:'#0F172A'}}>{rec.label}</Text><Text style={{fontSize:12,color:'#94A3B8',marginTop:1}}>{rec.text}</Text></View>
                  <Ionicons name="chevron-forward-outline" size={15} color="#CBD5E1"/>
                </TouchableOpacity>
              ))}
            </View>
          </>
        )}
      </ScrollView>
    );
  };


  // ─── AR view ──────────────────────────────────────────────────────────────────
  const arData = {
    projects:       projects,
    skills:         skills,
    certifications: certifications,
    workHistory:    workHistory,
    education:      education,
  }[arMode] || [];

  const renderARScene = () => (
    <View style={t.arContainer}>
      <View style={t.arModeBar}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{paddingHorizontal:12,gap:8}}>
          {['projects','skills','certifications','workHistory','education'].map(m=>(
            <TouchableOpacity key={m} style={[t.arModeBtn,arMode===m&&t.arModeBtnA]} onPress={()=>{setArMode(m);setCurrentModel(null);}}>
              <Ionicons name={SECTION_META[m]?.icon||'layers-outline'} size={15} color={arMode===m?'#FFF':SECTION_META[m]?.color||'#FFF'}/>
              <Text style={[t.arModeBtnT,arMode===m&&{color:'#FFF'}]}>{SECTION_META[m]?.label||m}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
      <View style={t.arScene}>
        <View style={t.arBg}>
          <Text style={t.arBgTitle}>AR Portfolio · {SECTION_META[arMode]?.label}</Text>
          <Text style={t.arBgSub}>{arData.length} items · tap to select</Text>
        </View>
        <View style={{flexDirection:'row',flexWrap:'wrap',padding:16,gap:10}}>
          {arData.map((item,idx)=>{
            const isSel = currentModel===idx;
            const mc    = SECTION_META[arMode]?.color||'#0e4e96';
            return (
              <TouchableOpacity key={item.id} style={[t.arItem,isSel&&{borderColor:mc,borderWidth:2,transform:[{scale:1.05}]}]} onPress={()=>setCurrentModel(isSel?null:idx)}>
                <View style={[t.arItemIcon,{backgroundColor:isSel?mc:'#EEF2FF'}]}>
                  <Ionicons name={SECTION_META[arMode]?.icon||'layers-outline'} size={18} color={isSel?'#FFF':mc}/>
                </View>
                <Text style={[t.arItemTitle,isSel&&{color:mc}]} numberOfLines={1}>{item.title||item.name||item.position||item.degree}</Text>
                <Text style={t.arItemSub} numberOfLines={1}>
                  {arMode==='skills'&&`${item.proficiency}%`}
                  {arMode==='projects'&&(item.category||item.status)}
                  {arMode==='workHistory'&&item.company}
                  {arMode==='certifications'&&item.issuer}
                  {arMode==='education'&&item.institution}
                </Text>
                {isSel&&(
                  <View style={t.arItemDetail}>
                    <Text style={{fontSize:11,color:'#FFF',textAlign:'center'}} numberOfLines={3}>{item.description||item.fieldOfStudy||`${item.proficiency||0}% proficiency`}</Text>
                    <TouchableOpacity style={{marginTop:6,backgroundColor:'#FFF',borderRadius:6,paddingHorizontal:10,paddingVertical:4}} onPress={()=>{setSelectedItem({...item,_cat:arMode});setShowItemDetails(true);}}>
                      <Text style={{fontSize:11,color:mc,fontWeight:'700'}}>View Details</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </TouchableOpacity>
            );
          })}
          {arData.length===0&&(
            <View style={{flex:1,alignItems:'center',paddingTop:40}}>
              <Ionicons name={SECTION_META[arMode]?.icon||'layers-outline'} size={40} color="rgba(255,255,255,0.4)"/>
              <Text style={{color:'rgba(255,255,255,0.6)',fontSize:14,marginTop:10}}>No {SECTION_META[arMode]?.label} to display</Text>
              <TouchableOpacity style={{marginTop:12,backgroundColor:'rgba(255,255,255,0.15)',borderRadius:8,paddingHorizontal:16,paddingVertical:8}} onPress={()=>{setIsARActive(false);openAdd(arMode);}}>
                <Text style={{color:'#FFF',fontWeight:'700',fontSize:13}}>+ Add {SECTION_META[arMode]?.label}</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
        {currentModel!==null&&(
          <View style={t.arControls}>
            <TouchableOpacity style={t.arCtrlBtn} onPress={()=>setModelScale(p=>Math.max(0.5,p-0.1))}><Ionicons name="remove" size={18} color="#FFF"/></TouchableOpacity>
            <Text style={t.arCtrlText}>Scale: {modelScale.toFixed(1)}</Text>
            <TouchableOpacity style={t.arCtrlBtn} onPress={()=>setModelScale(p=>Math.min(2,p+0.1))}><Ionicons name="add" size={18} color="#FFF"/></TouchableOpacity>
          </View>
        )}
      </View>
      <TouchableOpacity style={t.exitAR} onPress={()=>setIsARActive(false)}>
        <Ionicons name="close" size={20} color="#FFF"/>
        <Text style={t.exitARText}>Exit AR</Text>
      </TouchableOpacity>
    </View>
  );

  // ─── FILTER MODAL ─────────────────────────────────────────────────────────────
  const renderFilterModal = () => {
    const Sec = ({title}) => <Text style={t.fSecT}>{title}</Text>;
    const Chips = ({opts,sel,onSel}) => (
      <View style={t.chips}>
        {opts.map(o=><TouchableOpacity key={o} style={[t.chip,sel===o&&t.chipA]} onPress={()=>onSel(o)}><Text style={[t.chipT,sel===o&&t.chipTA]}>{o}</Text></TouchableOpacity>)}
      </View>
    );
    const clearAll = () => {
      setProjStatus('All');setProjCat('All');setProjSort('date');
      setSkillCat('All');setSkillLevel('All');setSkillSort('proficiency');
      setWorkEmpType('All');setWorkStatus('All');setWorkSort('date');
      setEduLevel('All');setEduStatus('All');
      setCertStatus('All');setCertCat('All');setCertSort('date');
    };
    return (
      <Modal visible={filterModal} transparent animationType="slide" onRequestClose={()=>setFilterModal(false)}>
        <View style={t.mOverlay}>
          <View style={t.mSheet}>
            <View style={t.mHdr}>
              <Text style={t.mTitle}>Filters & Sort — {SECTION_META[activeTab]?.label||activeTab}</Text>
              <TouchableOpacity onPress={()=>setFilterModal(false)}><Ionicons name="close" size={22} color="#64748B"/></TouchableOpacity>
            </View>
            <ScrollView contentContainerStyle={{padding:16,paddingBottom:20}}>
              {activeTab==='projects'&&(<><Sec title="Status"/><Chips opts={['All',...PROJ_STATUSES]} sel={projStatus} onSel={setProjStatus}/><Sec title="Category"/><Chips opts={['All',...PROJ_CATEGORIES]} sel={projCat} onSel={setProjCat}/><Sec title="Sort By"/><Chips opts={['date','title']} sel={projSort} onSel={setProjSort}/></>)}
              {activeTab==='skills'&&(<><Sec title="Category"/><Chips opts={['All',...SKILL_CATEGORIES]} sel={skillCat} onSel={setSkillCat}/><Sec title="Level"/><Chips opts={['All','Beginner','Intermediate','Advanced','Expert']} sel={skillLevel} onSel={setSkillLevel}/><Sec title="Sort By"/><Chips opts={['proficiency','name','category']} sel={skillSort} onSel={setSkillSort}/></>)}
              {activeTab==='workHistory'&&(<><Sec title="Employment Type"/><Chips opts={['All',...EMP_TYPES]} sel={workEmpType} onSel={setWorkEmpType}/><Sec title="Status"/><Chips opts={['All','Current','Previous']} sel={workStatus} onSel={setWorkStatus}/><Sec title="Sort By"/><Chips opts={['date','company','position']} sel={workSort} onSel={setWorkSort}/></>)}
              {activeTab==='education'&&(<><Sec title="Level"/><Chips opts={['All',...EDU_LEVELS]} sel={eduLevel} onSel={setEduLevel}/><Sec title="Status"/><Chips opts={['All',...EDU_STATUSES]} sel={eduStatus} onSel={setEduStatus}/></>)}
              {activeTab==='certifications'&&(<><Sec title="Status"/><Chips opts={['All',...CERT_STATUSES]} sel={certStatus} onSel={setCertStatus}/><Sec title="Sort By"/><Chips opts={['date','title']} sel={certSort} onSel={setCertSort}/></>)}
            </ScrollView>
            <View style={t.mFooter}>
              <TouchableOpacity style={t.mGhost} onPress={clearAll}><Text style={t.mGhostT}>Clear All</Text></TouchableOpacity>
              <TouchableOpacity style={t.mPrimary} onPress={()=>setFilterModal(false)}><Text style={t.mPrimaryT}>Apply</Text></TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    );
  };


  // ─── EDIT MODAL ───────────────────────────────────────────────────────────────
  const renderEditModal = () => {
    const isEdit = editMode==='edit';
    const meta   = SECTION_META[editCategory]||{};
    const F = ({label,fkey,placeholder,multiline,keyboard,required}) => (
      <View style={t.ff}>
        <Text style={t.fl}>{label}{required&&<Text style={{color:'#EF4444'}}> *</Text>}</Text>
        <TextInput style={[t.fi,multiline&&t.fia,formErrors[fkey]&&t.fierr]}
          value={toStr(formData[fkey])} onChangeText={v=>setFormData(p=>({...p,[fkey]:v}))}
          placeholder={placeholder} placeholderTextColor="#94A3B8"
          multiline={!!multiline} numberOfLines={multiline?4:1}
          keyboardType={keyboard||'default'} textAlignVertical={multiline?'top':'center'}/>
        {formErrors[fkey]&&<Text style={t.ferr}>{formErrors[fkey]}</Text>}
      </View>
    );
    const Chips = ({label,opts,fkey,selected}) => (
      <View style={t.ff}>
        <Text style={t.fl}>{label}</Text>
        <View style={t.chips}>
          {opts.map(o=><TouchableOpacity key={o} style={[t.chip,formData[fkey]===o&&t.chipA]} onPress={()=>setFormData(p=>({...p,[fkey]:o}))}>
            <Text style={[t.chipT,formData[fkey]===o&&t.chipTA]}>{o}</Text>
          </TouchableOpacity>)}
        </View>
      </View>
    );
    const labels={projects:'Project',skills:'Skill',certifications:'Certification',workHistory:'Work Experience',education:'Education'};
    return (
      <Modal visible={showEditModal} animationType="slide" onRequestClose={()=>setShowEditModal(false)}>
        <KeyboardAvoidingView style={{flex:1,backgroundColor:'#FFF'}} behavior={Platform.OS==='ios'?'padding':'height'}>
          {/* FIX: was paddingVertical:40 */}
          <View style={t.fullModalHdr}>
            <TouchableOpacity style={t.mIconBtn} onPress={()=>setShowEditModal(false)}><Ionicons name="close" size={22} color="#64748B"/></TouchableOpacity>
            <Text style={t.fullModalTitle}>{isEdit?'Edit':'Add'} {labels[editCategory]||editCategory}</Text>
            <TouchableOpacity style={[t.saveBtnFull,{backgroundColor:meta.color||'#0e4e96'},savingForm&&{opacity:0.6}]} onPress={saveForm} disabled={savingForm}>
              {savingForm?<ActivityIndicator size={14} color="#FFF"/>:<Text style={t.saveBtnFullT}>Save</Text>}
            </TouchableOpacity>
          </View>
          <ScrollView contentContainerStyle={{padding:20,paddingBottom:40}}>
            {editCategory==='projects'&&(<>
              <F label="Project Title"    fkey="title"       placeholder="e.g. Smart Energy Monitor"    required/>
              <F label="Description"      fkey="description" placeholder="What it does and your role…"  multiline required/>
              <F label="Client / Company" fkey="client"      placeholder="Client or organisation name"/>
              <Chips label="Category" opts={PROJ_CATEGORIES} fkey="category"/>
              <Chips label="Status"   opts={PROJ_STATUSES}   fkey="status"/>
              <View style={{flexDirection:'row',gap:12}}><View style={{flex:1}}><F label="Start Date" fkey="startDate" placeholder="Jan 2024"/></View><View style={{flex:1}}><F label="End Date" fkey="endDate" placeholder="Mar 2024"/></View></View>
              <F label="Project URL" fkey="url" placeholder="https://github.com/…"/>
              <View style={t.ff}><Text style={t.fl}>Technologies (comma-separated)</Text>
                <TextInput style={t.fi} value={(formData.technologies||[]).join(', ')}
                  onChangeText={v=>setFormData(p=>({...p,technologies:v.split(',').map(x=>x.trim()).filter(Boolean)}))}
                  placeholder="React, Node.js, PostgreSQL" placeholderTextColor="#94A3B8"/>
              </View>
              <F label="Budget (optional)"    fkey="budget"   placeholder="e.g. £250,000"/>
              <F label="Team Size (optional)" fkey="teamSize" placeholder="e.g. 8 members"/>
            </>)}
            {editCategory==='skills'&&(<>
              <F label="Skill Name" fkey="name" placeholder="e.g. AutoCAD, Python, HVAC" required/>
              <View style={t.ff}>
                <Text style={t.fl}>Proficiency: {formData.proficiency||50}% · {skillLevelLabel(formData.proficiency||50)}</Text>
                <View style={{marginTop:8}}>
                  <View style={t.profTrack}><View style={[t.profFill,{width:`${formData.proficiency||50}%`,backgroundColor:skillLevelColor(formData.proficiency||50)}]}/></View>
                  <View style={{flexDirection:'row',flexWrap:'wrap',gap:6,marginTop:10}}>
                    {[1,5,10].map(step=>(
                      <TouchableOpacity key={`-${step}`} style={t.profBtn} onPress={()=>setFormData(p=>({...p,proficiency:Math.max(0,(p.proficiency||50)-step)}))}><Text style={t.profBtnT}>-{step}</Text></TouchableOpacity>
                    ))}
                    {[1,5,10].map(step=>(
                      <TouchableOpacity key={`+${step}`} style={[t.profBtn,{backgroundColor:'#EFF6FF',borderColor:'#BFDBFE'}]} onPress={()=>setFormData(p=>({...p,proficiency:Math.min(100,(p.proficiency||50)+step)}))}><Text style={[t.profBtnT,{color:'#0e4e96'}]}>+{step}</Text></TouchableOpacity>
                    ))}
                  </View>
                </View>
                {formErrors.proficiency&&<Text style={t.ferr}>{formErrors.proficiency}</Text>}
              </View>
              <Chips label="Category" opts={SKILL_CATEGORIES} fkey="category"/>
              <F label="Description" fkey="description" placeholder="Describe your experience with this skill" multiline/>
              <F label="Years of Experience" fkey="experienceYears" placeholder="e.g. 3" keyboard="numeric"/>
            </>)}
            {editCategory==='certifications'&&(<>
              <F label="Certificate Title"    fkey="title"          placeholder="e.g. NEBOSH General Certificate" required/>
              <F label="Issuing Organisation" fkey="issuer"         placeholder="e.g. NEBOSH"                     required/>
              <F label="Description"          fkey="description"    placeholder="What it covers and its relevance" multiline/>
              <View style={{flexDirection:'row',gap:12}}><View style={{flex:1}}><F label="Issue Date" fkey="date" placeholder="June 2023" required/></View><View style={{flex:1}}><F label="Expiry" fkey="expiryDate" placeholder="No Expiry"/></View></View>
              <Chips label="Category" opts={['Technical','Professional','Management','Safety','Academic','Other']} fkey="category"/>
              <Chips label="Status"   opts={CERT_STATUSES} fkey="status"/>
              <F label="Credential ID"    fkey="credentialId"    placeholder="ABC-123456"/>
              <F label="Verification URL" fkey="verificationUrl" placeholder="https://verify.example.com"/>
            </>)}
            {editCategory==='workHistory'&&(<>
              <F label="Job Title / Position" fkey="position"    placeholder="e.g. Senior Engineer"          required/>
              <F label="Company"              fkey="company"     placeholder="e.g. ABC Engineering Ltd"      required/>
              <F label="Location"             fkey="location"    placeholder="e.g. London, UK"/>
              <View style={{flexDirection:'row',gap:12}}><View style={{flex:1}}><F label="Start Date" fkey="startDate" placeholder="Jan 2022" required/></View><View style={{flex:1}}>
                <Text style={t.fl}>End Date</Text>
                <TextInput style={t.fi} value={formData.currentlyWorking?'Present':toStr(formData.endDate)} onChangeText={v=>setFormData(p=>({...p,endDate:v,currentlyWorking:false}))} placeholder="Present" placeholderTextColor="#94A3B8"/>
              </View></View>
              <View style={{flexDirection:'row',alignItems:'center',justifyContent:'space-between',paddingVertical:10,marginBottom:12,borderBottomWidth:1,borderBottomColor:'#F1F5F9'}}>
                <Text style={t.fl}>I currently work here</Text>
                <TouchableOpacity style={[{width:48,height:26,borderRadius:13,padding:2,backgroundColor:formData.currentlyWorking?'#0e4e96':'#E5E7EB'}]} onPress={()=>setFormData(p=>({...p,currentlyWorking:!p.currentlyWorking,endDate:!p.currentlyWorking?'Present':p.endDate}))}>
                  <View style={{width:22,height:22,borderRadius:11,backgroundColor:'#FFF',elevation:2,alignSelf:formData.currentlyWorking?'flex-end':'flex-start'}}/>
                </TouchableOpacity>
              </View>
              <Chips label="Employment Type" opts={EMP_TYPES} fkey="employmentType"/>
              <F label="Description"       fkey="description"   placeholder="Key responsibilities and technologies" multiline/>
              <F label="Salary (optional)" fkey="salary"        placeholder="e.g. £50,000/yr"/>
              <View style={t.ff}><Text style={t.fl}>Key Achievements (one per line)</Text>
                <TextInput style={[t.fi,t.fia]} value={(formData.achievements||[]).join('\n')}
                  onChangeText={v=>setFormData(p=>({...p,achievements:v.split('\n').filter(x=>x.trim())}))}
                  placeholder={'• Reduced costs by 15%\n• Led team of 8 engineers'} placeholderTextColor="#94A3B8" multiline numberOfLines={4} textAlignVertical="top"/>
              </View>
            </>)}
            {editCategory==='education'&&(<>
              <F label="Degree / Qualification"  fkey="degree"        placeholder="e.g. BEng Mechanical Engineering" required/>
              <F label="Institution"             fkey="institution"   placeholder="e.g. University of Manchester"    required/>
              <F label="Field of Study"          fkey="fieldOfStudy"  placeholder="e.g. Mechanical Engineering"/>
              <Chips label="Level"  opts={EDU_LEVELS}   fkey="level"/>
              <Chips label="Status" opts={EDU_STATUSES} fkey="status"/>
              <F label="Location"    fkey="location"  placeholder="e.g. Manchester, UK"/>
              <View style={{flexDirection:'row',gap:12}}><View style={{flex:1}}><F label="Start" fkey="startDate" placeholder="2018"/></View><View style={{flex:1}}><F label="End" fkey="endDate" placeholder="2022"/></View></View>
              <F label="GPA / Grade"  fkey="gpa"         placeholder="e.g. 3.8 / First Class"/>
              <F label="Description"  fkey="description" placeholder="Relevant modules, thesis topic, activities…" multiline/>
              <View style={t.ff}><Text style={t.fl}>Achievements (one per line)</Text>
                <TextInput style={[t.fi,t.fia]} value={(formData.achievements||[]).join('\n')}
                  onChangeText={v=>setFormData(p=>({...p,achievements:v.split('\n').filter(x=>x.trim())}))}
                  placeholder={"• Dean's List\n• Research Assistant\n• Graduated with Distinction"} placeholderTextColor="#94A3B8" multiline numberOfLines={4} textAlignVertical="top"/>
              </View>
            </>)}
          </ScrollView>
        </KeyboardAvoidingView>
      </Modal>
    );
  };

  // ─── ITEM DETAIL MODAL ────────────────────────────────────────────────────────
  const renderDetailModal = () => {
    const item = selectedItem;
    if (!item) return null;
    const cat  = item._cat;
    const meta = SECTION_META[cat]||{};
    const name = item.title||item.name||item.position||item.degree||'Details';
    const Field = ({label,value}) => value?(
      <View style={{marginBottom:14}}><Text style={{fontSize:12,fontWeight:'700',color:'#94A3B8',textTransform:'uppercase',letterSpacing:0.5}}>{label}</Text><Text style={{fontSize:15,color:'#0F172A',marginTop:3,lineHeight:22}}>{value}</Text></View>
    ):null;
    return (
      <Modal visible={showItemDetails} animationType="slide" onRequestClose={()=>setShowItemDetails(false)}>
        <SafeAreaView style={{flex:1,backgroundColor:'#FFF'}}>
          <View style={t.fullModalHdr}>
            <TouchableOpacity style={t.mIconBtn} onPress={()=>setShowItemDetails(false)}><Ionicons name="close" size={22} color="#64748B"/></TouchableOpacity>
            <Text style={[t.fullModalTitle,{flex:1,textAlign:'left',marginLeft:8}]} numberOfLines={1}>{name}</Text>
            <TouchableOpacity style={t.mIconBtn} onPress={()=>{setShowItemDetails(false);openEdit(cat,item);}}>
              <Ionicons name="create-outline" size={20} color={meta.color||'#0e4e96'}/>
            </TouchableOpacity>
          </View>
          <ScrollView contentContainerStyle={{padding:20,paddingBottom:40}}>
            {/* Banner */}
            <LinearGradient colors={[meta.color||'#0e4e96',(meta.color||'#0e4e96')+'AA']} start={{x:0,y:0}} end={{x:1,y:1}} style={{borderRadius:16,padding:20,marginBottom:20,flexDirection:'row',alignItems:'center',gap:14}}>
              <View style={{width:52,height:52,borderRadius:14,backgroundColor:'rgba(255,255,255,0.25)',justifyContent:'center',alignItems:'center'}}>
                <Ionicons name={meta.icon||'layers-outline'} size={26} color="#FFF"/>
              </View>
              <View style={{flex:1}}>
                <Text style={{fontSize:18,fontWeight:'900',color:'#FFF'}}>{name}</Text>
                {cat==='projects'&&<Text style={{color:'rgba(255,255,255,0.8)',fontSize:13,marginTop:2}}>{item.category||''}</Text>}
                {cat==='skills'&&<Text style={{color:'rgba(255,255,255,0.8)',fontSize:13,marginTop:2}}>{item.category} · {skillLevelLabel(item.proficiency||0)}</Text>}
                {cat==='workHistory'&&<Text style={{color:'rgba(255,255,255,0.8)',fontSize:13,marginTop:2}}>{item.company}</Text>}
                {cat==='education'&&<Text style={{color:'rgba(255,255,255,0.8)',fontSize:13,marginTop:2}}>{item.institution}</Text>}
                {cat==='certifications'&&<Text style={{color:'rgba(255,255,255,0.8)',fontSize:13,marginTop:2}}>{item.issuer}</Text>}
              </View>
            </LinearGradient>
            {/* Skills proficiency */}
            {cat==='skills'&&(
              <View style={{backgroundColor:'#F8FAFC',borderRadius:12,padding:14,marginBottom:16,borderWidth:1,borderColor:'#E2E8F0'}}>
                <View style={{flexDirection:'row',justifyContent:'space-between',marginBottom:8}}>
                  <Text style={{fontSize:13,fontWeight:'700',color:'#374151'}}>Proficiency</Text>
                  <Text style={{fontSize:14,fontWeight:'800',color:skillLevelColor(item.proficiency||0)}}>{item.proficiency||0}%</Text>
                </View>
                <View style={{height:10,backgroundColor:'#E5E7EB',borderRadius:5,overflow:'hidden'}}>
                  <View style={{height:10,borderRadius:5,backgroundColor:skillLevelColor(item.proficiency||0),width:`${item.proficiency||0}%`}}/>
                </View>
                <Text style={{fontSize:11,color:skillLevelColor(item.proficiency||0),fontWeight:'700',marginTop:6}}>{skillLevelLabel(item.proficiency||0)}</Text>
              </View>
            )}
            {item.description&&<Field label="Description" value={item.description}/>}
            {/* Status badges */}
            {(item.status||item.employmentType)&&(
              <View style={{flexDirection:'row',flexWrap:'wrap',gap:8,marginBottom:14}}>
                {item.status&&<View style={{backgroundColor:(meta.color||'#0e4e96')+'22',borderRadius:8,paddingHorizontal:12,paddingVertical:6}}><Text style={{color:meta.color||'#0e4e96',fontWeight:'700',fontSize:13}}>{item.status}</Text></View>}
                {item.employmentType&&<View style={{backgroundColor:'#FFFBEB',borderRadius:8,paddingHorizontal:12,paddingVertical:6}}><Text style={{color:'#D97706',fontWeight:'700',fontSize:13}}>{item.employmentType}</Text></View>}
                {item.level&&<View style={{backgroundColor:'#F5F3FF',borderRadius:8,paddingHorizontal:12,paddingVertical:6}}><Text style={{color:'#8B5CF6',fontWeight:'700',fontSize:13}}>{item.level}</Text></View>}
              </View>
            )}
            {item.company&&<Field label="Company" value={item.company}/>}
            {item.institution&&<Field label="Institution" value={item.institution}/>}
            {item.fieldOfStudy&&<Field label="Field of Study" value={item.fieldOfStudy}/>}
            {item.issuer&&<Field label="Issuer" value={item.issuer}/>}
            {item.category&&cat!=='skills'&&<Field label="Category" value={item.category}/>}
            {item.location&&<Field label="Location" value={item.location}/>}
            {(item.startDate||item.date)&&<Field label="Date" value={[item.startDate||item.date,item.currentlyWorking?'Present':item.endDate||item.expiryDate].filter(Boolean).join(' – ')}/>}
            {item.gpa&&<Field label="GPA / Grade" value={item.gpa}/>}
            {item.salary&&<Field label="Salary" value={item.salary}/>}
            {item.credentialId&&<Field label="Credential ID" value={item.credentialId}/>}
            {item.verificationUrl&&<Field label="Verification URL" value={item.verificationUrl}/>}
            {item.url&&<Field label="Project URL" value={item.url}/>}
            {item.budget&&<Field label="Budget" value={item.budget}/>}
            {item.teamSize&&<Field label="Team Size" value={item.teamSize}/>}
            {item.technologies?.length>0&&(
              <View style={{marginBottom:14}}>
                <Text style={{fontSize:12,fontWeight:'700',color:'#94A3B8',textTransform:'uppercase',letterSpacing:0.5,marginBottom:8}}>Technologies</Text>
                <View style={{flexDirection:'row',flexWrap:'wrap',gap:6}}>
                  {item.technologies.map((tech,i)=><View key={i} style={{backgroundColor:'#EFF6FF',borderRadius:8,paddingHorizontal:10,paddingVertical:5}}><Text style={{fontSize:13,color:'#0e4e96',fontWeight:'600'}}>{tech}</Text></View>)}
                </View>
              </View>
            )}
            {item.achievements?.length>0&&(
              <View style={{marginBottom:14}}>
                <Text style={{fontSize:12,fontWeight:'700',color:'#94A3B8',textTransform:'uppercase',letterSpacing:0.5,marginBottom:8}}>Achievements</Text>
                {item.achievements.map((a,i)=><View key={i} style={{flexDirection:'row',alignItems:'flex-start',marginBottom:6,gap:6}}><Text style={{color:'#10B981',fontWeight:'800',marginTop:1}}>✓</Text><Text style={{flex:1,fontSize:14,color:'#374151',lineHeight:20}}>{a}</Text></View>)}
              </View>
            )}
          </ScrollView>
          <View style={{paddingHorizontal:20,paddingBottom:Platform.OS==='ios'?30:16,flexDirection:'row',gap:10,borderTopWidth:1,borderTopColor:'#E2E8F0'}}>
            <TouchableOpacity style={{flex:1,paddingVertical:13,borderRadius:12,backgroundColor:'#F1F5F9',alignItems:'center',borderWidth:1,borderColor:'#E2E8F0'}} onPress={()=>confirmDelete(cat,item)}>
              <Text style={{fontSize:14,fontWeight:'700',color:'#EF4444'}}>Delete</Text>
            </TouchableOpacity>
            <TouchableOpacity style={{flex:2,paddingVertical:13,borderRadius:12,backgroundColor:meta.color||'#0e4e96',alignItems:'center'}} onPress={()=>{setShowItemDetails(false);openEdit(cat,item);}}>
              <Text style={{fontSize:14,fontWeight:'700',color:'#FFF'}}>Edit</Text>
            </TouchableOpacity>
          </View>
        </SafeAreaView>
      </Modal>
    );
  };


  // ─── MAIN RENDER ──────────────────────────────────────────────────────────────

  if (isLoading) return (
    <View style={t.loadWrap}>
      <ActivityIndicator size={40} color="#0e4e96"/>
      <Text style={t.loadT}>Initialising Portfolio…</Text>
    </View>
  );

  const currentColor = SECTION_META[activeTab]?.color || '#0e4e96';

  const tabContent = () => {
    if (isARActive) return renderARScene();
    if (activeTab==='analytics') return renderAnalytics();
    const configs = {
      projects:       { data:filtered.projects,       render:renderProject, cat:'projects'       },
      skills:         { data:filtered.skills,         render:renderSkill,   cat:'skills'         },
      workHistory:    { data:filtered.workHistory,    render:renderWork,    cat:'workHistory'    },
      education:      { data:filtered.education,      render:renderEdu,     cat:'education'      },
      certifications: { data:filtered.certifications, render:renderCert,    cat:'certifications' },
    };
    const cfg = configs[activeTab];
    if (!cfg) return null;
    return <TabList data={cfg.data} renderItem={cfg.render} cat={cfg.cat}/>;
  };

  // Stats summary
  const stats = [
    {label:'Projects', count:projects.length,       color:'#0e4e96', tab:'projects'},
    {label:'Skills',   count:skills.length,         color:'#10B981', tab:'skills'},
    {label:'Work',     count:workHistory.length,     color:'#F59E0B', tab:'workHistory'},
    {label:'Edu',      count:education.length,       color:'#8B5CF6', tab:'education'},
    {label:'Certs',    count:certifications.length,  color:'#EF4444', tab:'certifications'},
  ];

  return (
    <SafeAreaView style={t.root}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96"/>

      {/* Header */}
      <View style={t.header}>
        <TouchableOpacity style={t.hdrBtn} onPress={()=>navigation.goBack()}>
          <Ionicons name="arrow-back" size={22} color="#FFF"/>
        </TouchableOpacity>
        <Text style={t.hdrTitle}>My Portfolio</Text>
        <View style={{flexDirection:'row',gap:6}}>
          <TouchableOpacity style={t.hdrBtn} onPress={()=>setShowSearch(p=>!p)}>
            <Ionicons name={showSearch?'close':'search'} size={20} color="#FFF"/>
          </TouchableOpacity>
          <TouchableOpacity style={t.hdrBtn} onPress={()=>{setIsARActive(p=>!p);setArMode(activeTab==='analytics'?'projects':activeTab);}}>
            <MaterialIcons name={isARActive?'3d-rotation':'view-in-ar'} size={20} color="#FFF"/>
          </TouchableOpacity>
          <TouchableOpacity style={[t.hdrBtn,syncingProfile&&{opacity:0.6}]} onPress={syncToProfile} disabled={syncingProfile}>
            {syncingProfile?<ActivityIndicator size={14} color="#FFF"/>:<Ionicons name="sync-outline" size={20} color="#FFF"/>}
          </TouchableOpacity>
        </View>
      </View>

      {/* Search bar */}
      {showSearch&&(
        <View style={t.searchWrap}>
          <Ionicons name="search-outline" size={17} color="#94A3B8"/>
          <TextInput style={t.searchIn} placeholder={`Search ${SECTION_META[activeTab]?.label||''}…`}
            value={search} onChangeText={setSearch} placeholderTextColor="#94A3B8" autoFocus/>
          {search?<TouchableOpacity onPress={()=>setSearch('')}><Ionicons name="close-circle" size={17} color="#94A3B8"/></TouchableOpacity>:null}
        </View>
      )}

      {/* Stats summary bar */}
      <View style={t.statsBar}>
        {stats.map(s=>(
          <TouchableOpacity key={s.tab} style={t.statItem} onPress={()=>setActiveTab(s.tab)}>
            <Text style={[t.statNum,{color:s.color}]}>{s.count}</Text>
            <Text style={t.statLbl}>{s.label}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Tab bar */}
      <View style={t.tabBar}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={t.tabBarC}>
          {TABS.map(tab=>{
            const m = SECTION_META[tab];
            const isA = activeTab===tab&&!isARActive;
            return(
              <TouchableOpacity key={tab} style={[t.tabBtn,isA&&{borderBottomColor:m.color,borderBottomWidth:3}]} onPress={()=>{setActiveTab(tab);setIsARActive(false);}}>
                <Ionicons name={m.icon} size={14} color={isA?m.color:'#94A3B8'}/>
                <Text style={[t.tabBtnT,isA&&{color:m.color}]}>{m.label}</Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </View>

      {/* Tab header row with count + filter button */}
      {!isARActive&&activeTab!=='analytics'&&(
        <View style={t.tabHdr}>
          <Text style={t.tabHdrTitle}>
            {SECTION_META[activeTab]?.label} <Text style={{color:currentColor}}>({(filtered[activeTab]||[]).length})</Text>
          </Text>
          <View style={{flexDirection:'row',gap:8}}>
            <TouchableOpacity style={t.filterBtnSm} onPress={()=>setFilterModal(true)}>
              <Ionicons name="filter-outline" size={16} color={currentColor}/>
              <Text style={[t.filterBtnSmT,{color:currentColor}]}>Filter</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[t.addBtnSm,{backgroundColor:currentColor}]} onPress={()=>openAdd(activeTab)}>
              <Ionicons name="add" size={16} color="#FFF"/>
              <Text style={t.addBtnSmT}>Add</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Content */}
      <Animated.View style={{flex:1,backgroundColor:'#F8FAFC',opacity:fadeAnim,transform:[{scale:scaleAnim}]}}>
        {tabContent()}
      </Animated.View>

      {/* FAB */}
      {!isARActive&&(
        <TouchableOpacity style={[t.fab,{backgroundColor:currentColor}]} onPress={()=>setShowAddMenu(true)}>
          <Ionicons name="add" size={28} color="#FFF"/>
        </TouchableOpacity>
      )}

      {/* Add menu */}
      <Modal visible={showAddMenu} transparent animationType="fade" onRequestClose={()=>setShowAddMenu(false)}>
        <TouchableOpacity style={t.addMenuOverlay} activeOpacity={1} onPress={()=>setShowAddMenu(false)}>
          <View style={t.addMenu}>
            <View style={t.addMenuHdr}>
              <Text style={t.addMenuTitle}>Add to Portfolio</Text>
              <TouchableOpacity onPress={()=>setShowAddMenu(false)}><Ionicons name="close" size={20} color="#64748B"/></TouchableOpacity>
            </View>
            {Object.entries(SECTION_META).filter(([k])=>k!=='analytics').map(([k,m])=>(
              <TouchableOpacity key={k} style={t.addMenuItem} onPress={()=>openAdd(k)}>
                <View style={[t.addMenuIcon,{backgroundColor:m.color}]}>
                  <Ionicons name={m.icon} size={20} color="#FFF"/>
                </View>
                <Text style={t.addMenuLabel}>Add {m.label}</Text>
                <Ionicons name="chevron-forward" size={18} color="#CBD5E1"/>
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Tutorial */}
      <Modal visible={showTutorial} transparent animationType="fade" onRequestClose={()=>setShowTutorial(false)}>
        <View style={t.tutOverlay}>
          <View style={t.tutCard}>
            <Text style={t.tutTitle}>AR Portfolio Mode</Text>
            <Text style={t.tutText}>View your portfolio items in an immersive layout. Tap items to see details, use mode buttons to switch categories, and scale controls to resize selected items.</Text>
            <TouchableOpacity style={t.tutBtn} onPress={()=>setShowTutorial(false)}><Text style={t.tutBtnT}>Got it!</Text></TouchableOpacity>
          </View>
        </View>
      </Modal>

      {renderFilterModal()}
      {renderEditModal()}
      {renderDetailModal()}
    </SafeAreaView>
  );
}

const t = StyleSheet.create({
  root:       { flex:1, backgroundColor:'#0e4e96' },
  loadWrap:   { flex:1, justifyContent:'center', alignItems:'center', backgroundColor:'#F8FAFC' },
  loadT:      { marginTop:14, fontSize:16, color:'#64748B', fontWeight:'500' },

  // Header
  header:     { flexDirection:'row', alignItems:'center', paddingHorizontal:16, paddingVertical:12, backgroundColor:'#0e4e96', gap:8 },
  hdrBtn:     { width:36, height:36, borderRadius:10, backgroundColor:'rgba(255,255,255,0.15)', justifyContent:'center', alignItems:'center' },
  hdrTitle:   { flex:1, textAlign:'center', color:'#FFF', fontSize:18, fontWeight:'700' },

  // Search
  searchWrap: { flexDirection:'row', alignItems:'center', backgroundColor:'#FFF', paddingHorizontal:14, paddingVertical:10, borderBottomWidth:1, borderBottomColor:'#E2E8F0', gap:8 },
  searchIn:   { flex:1, fontSize:14, color:'#0F172A' },

  // Stats bar
  statsBar:   { flexDirection:'row', backgroundColor:'#0a3d7a', paddingVertical:10 },
  statItem:   { flex:1, alignItems:'center' },
  statNum:    { fontSize:17, fontWeight:'900' },
  statLbl:    { fontSize:10, color:'rgba(255,255,255,0.65)', fontWeight:'600', marginTop:1 },

  // Tab bar
  tabBar:     { backgroundColor:'#FFF', borderBottomWidth:1, borderBottomColor:'#E2E8F0' },
  tabBarC:    { paddingHorizontal:8, paddingVertical:6 },
  tabBtn:     { flexDirection:'row', alignItems:'center', paddingHorizontal:12, paddingVertical:8, marginRight:4, gap:4, borderBottomWidth:3, borderBottomColor:'transparent' },
  tabBtnT:    { fontSize:11, color:'#94A3B8', fontWeight:'700' },

  // Tab header
  tabHdr:     { flexDirection:'row', alignItems:'center', justifyContent:'space-between', paddingHorizontal:14, paddingVertical:10, backgroundColor:'#FFF', borderBottomWidth:1, borderBottomColor:'#E2E8F0' },
  tabHdrTitle:{ fontSize:15, fontWeight:'700', color:'#0F172A' },
  filterBtnSm:{ flexDirection:'row', alignItems:'center', gap:4, paddingHorizontal:12, paddingVertical:7, borderRadius:10, backgroundColor:'#EFF6FF', borderWidth:1, borderColor:'#BFDBFE' },
  filterBtnSmT:{ fontSize:12, fontWeight:'700' },
  addBtnSm:   { flexDirection:'row', alignItems:'center', gap:4, paddingHorizontal:12, paddingVertical:7, borderRadius:10 },
  addBtnSmT:  { fontSize:12, fontWeight:'700', color:'#FFF' },

  // List
  list:       { paddingHorizontal:14, paddingTop:12, paddingBottom:90 },

  // Cards — FIX: was padding:46
  card:       { backgroundColor:'#FFF', borderRadius:16, padding:14, marginBottom:12, borderWidth:1, borderColor:'#E2E8F0', elevation:2, shadowColor:'#000', shadowOpacity:0.04, shadowRadius:6, shadowOffset:{width:0,height:2} },
  cardRow:    { flexDirection:'row', alignItems:'flex-start', gap:10 },
  cardIcon:   { width:40, height:40, borderRadius:10, justifyContent:'center', alignItems:'center', flexShrink:0 },
  cardTitle:  { fontSize:15, fontWeight:'800', color:'#0F172A' },
  cardSub:    { fontSize:13, color:'#64748B', fontWeight:'600', marginTop:2 },
  metaRow:    { flexDirection:'row', flexWrap:'wrap', gap:6, marginTop:6 },
  cardBtns:   { gap:4 },
  editBtn:    { width:30, height:30, borderRadius:8, backgroundColor:'#EFF6FF', borderWidth:1, borderColor:'#BFDBFE', justifyContent:'center', alignItems:'center' },
  delBtn:     { width:30, height:30, borderRadius:8, backgroundColor:'#FEF2F2', borderWidth:1, borderColor:'#FECACA', justifyContent:'center', alignItems:'center' },
  cardDesc:   { color:'#475569', fontSize:13, lineHeight:18, marginTop:8 },
  cardFooter: { flexDirection:'row', alignItems:'center', flexWrap:'wrap', marginTop:8, gap:3 },
  cardFootT:  { fontSize:11, color:'#94A3B8', marginLeft:3, marginRight:6 },
  cardNote:   { fontSize:11, color:'#94A3B8', fontStyle:'italic', marginTop:4 },
  tagRow:     { flexDirection:'row', flexWrap:'wrap', gap:6, marginTop:8 },

  // Badge & Tag
  badge:      { borderRadius:20, borderWidth:1, paddingHorizontal:8, paddingVertical:3 },
  badgeT:     { fontSize:11, fontWeight:'700' },
  tag:        { borderRadius:14, borderWidth:1, paddingHorizontal:8, paddingVertical:3 },
  tagT:       { fontSize:11, fontWeight:'600' },

  // Progress
  progRow:    { flexDirection:'row', alignItems:'center', gap:8, marginTop:10 },
  progTrack:  { flex:1, height:6, backgroundColor:'#E5E7EB', borderRadius:3, overflow:'hidden' },
  progFill:   { height:6, borderRadius:3 },
  progPct:    { fontSize:12, fontWeight:'700', width:32, textAlign:'right' },

  // Empty
  empty:      { alignItems:'center', paddingVertical:40, paddingHorizontal:20 },
  emptyT:     { fontSize:15, fontWeight:'800', color:'#0F172A', marginTop:12 },
  emptyS:     { fontSize:13, color:'#94A3B8', textAlign:'center', marginTop:6, lineHeight:18 },
  emptyBtn:   { marginTop:16, paddingHorizontal:24, paddingVertical:12, borderRadius:12 },
  emptyBtnT:  { color:'#FFF', fontWeight:'700', fontSize:14 },

  // FAB
  fab:        { position:'absolute', bottom:24, right:20, width:56, height:56, borderRadius:28, justifyContent:'center', alignItems:'center', elevation:8, shadowColor:'#000', shadowOpacity:0.3, shadowRadius:8, shadowOffset:{width:0,height:4} },

  // Add menu
  addMenuOverlay:{ flex:1, backgroundColor:'rgba(0,0,0,0.5)', justifyContent:'flex-end' },
  addMenu:    { backgroundColor:'#FFF', borderTopLeftRadius:24, borderTopRightRadius:24, paddingBottom:Platform.OS==='ios'?36:20 },
  addMenuHdr: { flexDirection:'row', justifyContent:'space-between', alignItems:'center', padding:18, borderBottomWidth:1, borderBottomColor:'#E2E8F0' },
  addMenuTitle:{ fontSize:17, fontWeight:'800', color:'#0F172A' },
  addMenuItem:{ flexDirection:'row', alignItems:'center', paddingVertical:14, paddingHorizontal:18, borderBottomWidth:1, borderBottomColor:'#F1F5F9', gap:14 },
  addMenuIcon:{ width:40, height:40, borderRadius:10, justifyContent:'center', alignItems:'center' },
  addMenuLabel:{ flex:1, fontSize:15, fontWeight:'600', color:'#0F172A' },

  // Analytics
  periodRow:  { flexDirection:'row', paddingHorizontal:14, paddingVertical:10, gap:8, backgroundColor:'#FFF', borderBottomWidth:1, borderBottomColor:'#E2E8F0' },
  periodBtn:  { flex:1, paddingVertical:8, borderRadius:10, backgroundColor:'#F1F5F9', alignItems:'center' },
  periodBtnA: { backgroundColor:'#0e4e96' },
  periodBtnT: { fontSize:12, fontWeight:'700', color:'#64748B' },
  periodBtnTA:{ color:'#FFF' },
  aTabRow:    { flexDirection:'row', paddingHorizontal:14, paddingVertical:8, gap:8, backgroundColor:'#F8FAFC' },
  aTab:       { flex:1, flexDirection:'row', justifyContent:'center', alignItems:'center', paddingVertical:8, borderRadius:10, gap:4 },
  aTabA:      { backgroundColor:'#FFF', elevation:2, shadowColor:'#000', shadowOpacity:0.06, shadowRadius:4 },
  aTabT:      { fontSize:11, fontWeight:'700', color:'#94A3B8' },
  aTabTA:     { color:'#667eea' },
  aCard:      { backgroundColor:'#FFF', borderRadius:16, margin:14, marginBottom:0, padding:16, borderWidth:1, borderColor:'#E2E8F0' },
  aCardTitle: { fontSize:15, fontWeight:'800', color:'#0F172A', marginBottom:10 },
  complTrack: { height:10, backgroundColor:'#E5E7EB', borderRadius:5, overflow:'hidden' },
  complFill:  { height:10, borderRadius:5, backgroundColor:'#10B981' },
  metricsGrid:{ flexDirection:'row', flexWrap:'wrap', paddingHorizontal:14, paddingTop:14, gap:10 },
  metricCard: { width:(width-48)/2, borderRadius:14, padding:14, alignItems:'center', gap:4 },
  metricVal:  { fontSize:26, fontWeight:'900', color:'#FFF', marginTop:4 },
  metricLbl:  { fontSize:11, color:'rgba(255,255,255,0.85)', fontWeight:'600' },
  secProgRow: { flexDirection:'row', alignItems:'center', marginBottom:10 },
  compRow:    { flexDirection:'row', alignItems:'center', paddingVertical:8, borderBottomWidth:1, borderBottomColor:'#F1F5F9', gap:10 },
  compRank:   { width:24, height:24, borderRadius:12, backgroundColor:'#EFF6FF', justifyContent:'center', alignItems:'center' },
  compRankT:  { fontSize:11, fontWeight:'800', color:'#0e4e96' },
  distRow:    { flexDirection:'row', alignItems:'center', marginBottom:8, gap:8 },
  distDot:    { width:10, height:10, borderRadius:5 },

  // Chips/chips in modals
  chips:      { flexDirection:'row', flexWrap:'wrap', gap:8, marginTop:6, marginBottom:8 },
  chip:       { paddingHorizontal:12, paddingVertical:7, borderRadius:20, backgroundColor:'#F1F5F9', borderWidth:1, borderColor:'#E2E8F0' },
  chipA:      { backgroundColor:'#0e4e96', borderColor:'#0e4e96' },
  chipT:      { fontSize:12, color:'#374151', fontWeight:'500' },
  chipTA:     { color:'#FFF' },

  // Form
  ff:         { marginBottom:14 },
  fl:         { fontSize:13, fontWeight:'700', color:'#0F172A', marginBottom:6 },
  fi:         { borderWidth:1, borderColor:'#E2E8F0', backgroundColor:'#F8FAFC', borderRadius:12, paddingHorizontal:12, paddingVertical:12, fontSize:14, color:'#0F172A' },
  fia:        { minHeight:96, textAlignVertical:'top' },
  fierr:      { borderColor:'#EF4444' },
  ferr:       { fontSize:11, color:'#EF4444', marginTop:3 },
  profTrack:  { height:10, backgroundColor:'#E5E7EB', borderRadius:5, overflow:'hidden' },
  profFill:   { height:10, borderRadius:5 },
  profBtn:    { paddingHorizontal:12, paddingVertical:6, borderRadius:8, backgroundColor:'#FEF2F2', borderWidth:1, borderColor:'#FECACA' },
  profBtnT:   { fontSize:12, fontWeight:'700', color:'#DC2626' },

  // Modals
  mOverlay:   { flex:1, backgroundColor:'rgba(0,0,0,0.5)', justifyContent:'flex-end' },
  mSheet:     { backgroundColor:'#FFF', borderTopLeftRadius:24, borderTopRightRadius:24, maxHeight:height*0.9, overflow:'hidden' },
  mHdr:       { flexDirection:'row', justifyContent:'space-between', alignItems:'center', padding:18, borderBottomWidth:1, borderBottomColor:'#E2E8F0' },
  mTitle:     { fontSize:16, fontWeight:'800', color:'#0F172A', flex:1 },
  mFooter:    { flexDirection:'row', gap:12, padding:16, borderTopWidth:1, borderTopColor:'#E2E8F0' },
  mGhost:     { flex:1, paddingVertical:13, borderRadius:12, backgroundColor:'#F1F5F9', borderWidth:1, borderColor:'#E2E8F0', alignItems:'center' },
  mGhostT:    { fontSize:14, fontWeight:'700', color:'#374151' },
  mPrimary:   { flex:2, paddingVertical:13, borderRadius:12, backgroundColor:'#0e4e96', alignItems:'center' },
  mPrimaryT:  { fontSize:14, fontWeight:'700', color:'#FFF' },
  // FIX: full-screen modal header — was paddingVertical:40
  fullModalHdr:{ flexDirection:'row', alignItems:'center', paddingHorizontal:16, paddingVertical:14, borderBottomWidth:1, borderBottomColor:'#E2E8F0', backgroundColor:'#FFF' },
  fullModalTitle:{ flex:1, textAlign:'center', fontSize:17, fontWeight:'800', color:'#0F172A' },
  mIconBtn:   { width:36, height:36, borderRadius:10, backgroundColor:'#F1F5F9', justifyContent:'center', alignItems:'center' },
  saveBtnFull:{ paddingHorizontal:16, paddingVertical:9, borderRadius:10 },
  saveBtnFullT:{ color:'#FFF', fontWeight:'700', fontSize:14 },
  fSecT:      { fontSize:13, fontWeight:'700', color:'#0F172A', marginTop:14, marginBottom:6 },

  // AR
  arContainer:{ flex:1, backgroundColor:'#090e1e' },
  arModeBar:  { paddingVertical:12, backgroundColor:'rgba(0,0,0,0.7)', borderBottomWidth:1, borderBottomColor:'rgba(255,255,255,0.1)' },
  arModeBtn:  { flexDirection:'row', alignItems:'center', paddingHorizontal:14, paddingVertical:8, borderRadius:20, backgroundColor:'rgba(255,255,255,0.1)', marginRight:8, gap:6 },
  arModeBtnA: { backgroundColor:'#0e4e96' },
  arModeBtnT: { fontSize:12, color:'rgba(255,255,255,0.7)', fontWeight:'600' },
  arScene:    { flex:1 },
  arBg:       { position:'absolute', top:0, left:0, right:0, alignItems:'center', paddingTop:24 },
  arBgTitle:  { fontSize:18, color:'#FFF', fontWeight:'700', opacity:0.4 },
  arBgSub:    { fontSize:13, color:'#FFF', opacity:0.3, marginTop:4 },
  arItem:     { width:(width-52)/3, minHeight:110, borderRadius:14, backgroundColor:'rgba(255,255,255,0.07)', borderWidth:1, borderColor:'rgba(255,255,255,0.1)', padding:10, alignItems:'center', justifyContent:'center' },
  arItemIcon: { width:38, height:38, borderRadius:10, justifyContent:'center', alignItems:'center', marginBottom:6 },
  arItemTitle:{ fontSize:11, color:'#FFF', fontWeight:'700', textAlign:'center', lineHeight:14 },
  arItemSub:  { fontSize:10, color:'rgba(255,255,255,0.5)', textAlign:'center', marginTop:2 },
  arItemDetail:{ marginTop:8, padding:8, backgroundColor:'rgba(0,0,0,0.7)', borderRadius:8, width:'100%', alignItems:'center' },
  arControls: { position:'absolute', bottom:80, left:20, right:20, flexDirection:'row', justifyContent:'center', alignItems:'center', backgroundColor:'rgba(0,0,0,0.8)', borderRadius:12, paddingVertical:12, paddingHorizontal:20, gap:16 },
  arCtrlBtn:  { width:38, height:38, borderRadius:19, backgroundColor:'#0e4e96', justifyContent:'center', alignItems:'center' },
  arCtrlText: { fontSize:14, color:'#FFF', fontWeight:'600' },
  exitAR:     { position:'absolute', top:60, right:16, flexDirection:'row', alignItems:'center', backgroundColor:'rgba(0,0,0,0.8)', paddingHorizontal:14, paddingVertical:10, borderRadius:20, gap:6 },
  exitARText: { fontSize:13, color:'#FFF', fontWeight:'600' },

  // Tutorial
  tutOverlay: { flex:1, backgroundColor:'rgba(0,0,0,0.75)', justifyContent:'center', alignItems:'center', paddingHorizontal:30 },
  tutCard:    { backgroundColor:'#FFF', borderRadius:20, padding:24, alignItems:'center', width:'100%' },
  tutTitle:   { fontSize:20, fontWeight:'700', color:'#0F172A', marginBottom:12, textAlign:'center' },
  tutText:    { fontSize:14, color:'#64748B', lineHeight:22, textAlign:'center', marginBottom:16 },
  tutBtn:     { backgroundColor:'#0e4e96', paddingHorizontal:24, paddingVertical:12, borderRadius:10 },
  tutBtnT:    { color:'#FFF', fontWeight:'700', fontSize:15 },
});
